import { InputAdornment } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { AdditionalItemRendererProps } from '@/components/form/formAwareInputs/FormAwareInput.types';
import {
  FormAwareTypeaheadSelectInput,
  FormAwareTypeaheadSelectInputProps,
} from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { User01Icon } from '@/components/icons/User01Icon';
import { useFormContext } from '@/components/react-hook-form';
import { MatchableKind } from '@/modules/aiSuggestions/aiSuggestions.types';
import { LocatableFormAwareInput } from '@/modules/aiSuggestions/components/forms/LocatableFormAwareInput';
import { PathsOf } from '@/types/subform';

import {
  BasicInformationSubformType,
  BasicInformationSubformType_SingleGrantor,
  BasicInformationSubformType_WithGrantors,
  Fields as BasicInformationSubformFields,
} from '../../BasicInformationSubform/BasicInformationSubform.types';
import { TestamentaryEntityFormShape } from '../../testamentaryEntities/shared/testamentaryEntity.types';
import {
  Fields as TrustDetailsSubformFields,
  TrustDetailsSubformType,
  TrustDetailsSubformType_TrusteesAndSuccessorTrustees,
} from '../../TrustDetailsSubform/TrustDetailsSubform.types';
import { TrusteeDropdown_HouseholdFragment } from './graphql/TrusteeDropdown.generated';
import { TrusteeSelectorModal } from './TrusteeSelectorModal';

type TrusteeDropdownFormShape =
  | BasicInformationSubformFields
  | TrustDetailsSubformFields
  | TestamentaryEntityFormShape;

interface TrusteeDropdownProps {
  label: string;
  fieldName: PathsOf<TrusteeDropdownFormShape>;
  options: TypeaheadSelectInputOption<string>[];
  householdId: string;
  contextualHelp?: JSX.Element;
  hideLabel?: boolean;
  labelIconEnd?: React.ReactNode;
  // Optional parameter to associate the dropdown field with a suggestion kind.
  // If given, this will be used to show a suggestion locator icon for the field.
  suggestionKind?: MatchableKind;
  // Block navigation for the parent form after creating a new individual/organization.
  blockNavigationAfterCreate?: boolean;
  additionalItemRendererProps?: AdditionalItemRendererProps;
}

export function getTrusteeOptionsWithSelectedDisabled({
  possibleOptions,
  selectedIds,
}: {
  possibleOptions: ReturnType<typeof getOptionsFromData>;
  selectedIds: string[];
}) {
  return possibleOptions.map((option) => ({
    ...option,
    disabled: selectedIds.includes(option.value),
  }));
}

export function getOptionsFromData({
  data,
  subformValues,
}: {
  data: TrusteeDropdown_HouseholdFragment | null;
  subformValues?:
    | BasicInformationSubformType
    | BasicInformationSubformType_SingleGrantor
    | BasicInformationSubformType_WithGrantors
    | TrustDetailsSubformType
    | TrustDetailsSubformType_TrusteesAndSuccessorTrustees;
}): TypeaheadSelectInputOption<string>[] {
  if (!data) return [];

  const selectedIds: string[] = [];

  if (subformValues && 'trustees' in subformValues) {
    subformValues.trustees.forEach((t) => {
      selectedIds.push(t.trusteeId);
    });
  }

  if (subformValues && 'controllingParties' in subformValues) {
    (subformValues.controllingParties || []).forEach((t) => {
      selectedIds.push(t.controllingPartyId);
    });
  }

  const trusteeOptionsClients = (() => {
    return (
      data.clientProfiles?.map((t) => ({
        display: t.displayName,
        value: t.id,
        groupName: 'Individuals',
      })) ?? []
    );
  })();

  const trusteeOptionsOrgs = (() => {
    return (
      data.clientOrganizations?.map((t) => ({
        display: t.name,
        value: t.id,
        groupName: 'Organizations',
      })) ?? []
    );
  })();

  const possibleOptions = [
    ...trusteeOptionsClients.sort((a, b) => a.display.localeCompare(b.display)),
    ...trusteeOptionsOrgs.sort((a, b) => a.display.localeCompare(b.display)),
  ];

  return getTrusteeOptionsWithSelectedDisabled({
    possibleOptions,
    selectedIds,
  });
}

export function TrusteeDropdown({
  label,
  fieldName,
  options,
  householdId,
  hideLabel,
  contextualHelp,
  labelIconEnd,
  suggestionKind,
  blockNavigationAfterCreate,
  additionalItemRendererProps,
}: TrusteeDropdownProps) {
  const { control, setShouldBlockNavigation } =
    useFormContext<TrusteeDropdownFormShape>();
  const [newlyCreatedId, setNewlyCreatedId] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (blockNavigationAfterCreate && newlyCreatedId && !isOpen) {
      // Closing the modal after creating a new individual/organization should
      // block navigation for the parent form.
      setShouldBlockNavigation(true);
      setNewlyCreatedId('');
    }
  }, [
    newlyCreatedId,
    setShouldBlockNavigation,
    isOpen,
    blockNavigationAfterCreate,
  ]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const inputProps = useMemo(
    () => ({
      control,
      label,
      hideLabel,
      fieldName,
      contextualHelp,
      addNewOption: {
        onClick: () => setIsOpen(true),
      },
      options,
      placeholder: `Search ${label.toLowerCase()}`,
      groupBy: (option: TypeaheadSelectInputOption<string>) =>
        option.groupName ?? '',
      startAdornment: (
        <InputAdornment position="start">
          <User01Icon size={16} />
        </InputAdornment>
      ),
      labelIconEnd,
    }),
    [
      contextualHelp,
      control,
      fieldName,
      hideLabel,
      label,
      labelIconEnd,
      options,
    ]
  );

  return (
    <>
      {/* TODO: I think this can be replaced with the AddNewAnythingModal? */}
      <TrusteeSelectorModal
        isOpen={isOpen}
        handleClose={handleClose}
        householdId={householdId}
        onAfterCreate={setNewlyCreatedId}
      />
      {suggestionKind ? (
        <LocatableFormAwareInput<
          TrusteeDropdownFormShape,
          FormAwareTypeaheadSelectInputProps<TrusteeDropdownFormShape>
        >
          FormAwareElement={FormAwareTypeaheadSelectInput}
          suggestionKind={suggestionKind}
          additionalItemRendererProps={additionalItemRendererProps}
          {...inputProps}
        />
      ) : (
        <FormAwareTypeaheadSelectInput {...inputProps} />
      )}
    </>
  );
}
